import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { sanitizeAnalyticsPath } from '@/lib/analytics/analytics-path';
import { GA_MEASUREMENT_ID } from '@/lib/analytics/config';
import { onConsentChange, readConsent } from '@/lib/analytics/consent';
import type { ConsentValue } from '@/lib/analytics/consent';
import {
  disableGoogleAnalytics,
  loadGoogleAnalytics,
  trackPageView,
} from '@/lib/analytics/google-analytics';

/**
 * Loads Google Analytics once the visitor accepted cookies, and reports every
 * route change of the single-page app as a page view.
 */
export function useGoogleAnalytics(measurementId: string = GA_MEASUREMENT_ID): void {
  const { pathname } = useLocation();
  const [consent, setConsent] = useState<ConsentValue | null>(readConsent);

  useEffect(() => onConsentChange(setConsent), []);

  useEffect(() => {
    if (!measurementId) return;
    if (consent === 'accepted') loadGoogleAnalytics(measurementId);
    if (consent === 'rejected') disableGoogleAnalytics(measurementId);
  }, [consent, measurementId]);

  const enabled = Boolean(measurementId) && consent === 'accepted';

  useEffect(() => {
    if (!enabled) return;
    trackPageView(measurementId, sanitizeAnalyticsPath(pathname), document.title);
  }, [enabled, measurementId, pathname]);
}
